import { Product, OrderItem, OrderType, PaymentCondition, TaxRule } from '../types/orders';

// Tax calculation constants (alinhado à planilha: aba DADOS)
const DISCOUNT_AVISTA = 0.4559; // 45,59% desconto padrão para pagamento à vista (fallback)
const DISCOUNT_APRAZO = 0.412; // 41,20% desconto padrão para pagamento a prazo (fallback)
// Divisor da coluna S/P/Q (aba "espelho nfe saida"): 1 - 0,0165 (PIS) - 0,076 (COFINS) - ICMS.
// A 3ª parcela (ICMS) NÃO é fixa — varia pelo estado de destino do pedido: 12% quando é
// dentro do PR (mesmo estado da Salamander), 4% pros demais (ver getIcmsRate/state_icms).
// CONFIRMADO em 21/08: as duas planilhas de referência do cliente com fórmula viva no
// Excel usam essa mesma estrutura — só que ambas tinham "0,04" fixo na fórmula (sem
// variar por UF), então só batiam pra pedido interestadual. O pedido que motivou essa
// investigação (BELGA CWB, 19/08) é PR->PR — a planilha dele usa 12% nessa parcela (a
// alíquota PR da tabela state_icms), não os 4% que o sistema usava fixo antes desse
// fix. Ver docs/RULEBOOK.md §4 pro histórico completo (inclui uma reversão no meio).
const ICMS_IMPORTADO = 0.04; // 4% fallback (outros estados)
const ICMS_INTERNO_PR = 0.12; // 12% fallback (PR)
const DIFAL_RATE = 0.07; // Difference to be paid (18% - 12% or 18% - 4%)

/** Alíquotas ICMS vindas da API (GET /taxes/config). rate em percentual (4, 12). */
let currentIcmsRates: Array<{ state: string; rate: number }> | null = null;

/** Define as alíquotas de ICMS dinâmicas (icmsRates do config). rate em percentual. */
export function setIcmsRates(rates: Array<{ state: string; rate: number }> | null) {
  currentIcmsRates = rates;
}

// Company info for tax calculations
export const companyInfo = {
  uf: 'PR',
  razaoSocial: 'Salamander Industria e Comercio LTDA',
  cnpj: '00.000.000/0001-00',
};

/** Divisor CPF por UF (coluna G da aba DADOS): PR = 0,925; demais = 0,87. */
const CPF_DIVISOR_PR = 0.925;
const CPF_DIVISOR_OTHERS = 0.87;

/**
 * Retorna o divisor da coluna G (DADOS) para o estado: preço CPF = preço_tabela / divisor.
 */
export function getCpFPriceDivisorByUF(uf: string): number {
  return (uf || '').toUpperCase().trim() === companyInfo.uf ? CPF_DIVISOR_PR : CPF_DIVISOR_OTHERS;
}

/**
 * Preço base para cálculo do item: PJ = preço da tabela; PF (CPF) = preço_tabela / divisor(UF).
 * Espelho da planilha: PJ = mesmo da tabela; CPF = fórmula com ÍNDICE/CORRESP na coluna G.
 */
export function getBasePriceForOrder(
  tablePrice: number,
  orderUf: string,
  isConsumerFinal: boolean
): number {
  if (!isConsumerFinal) return tablePrice;
  const divisor = getCpFPriceDivisorByUF(orderUf);
  return divisor > 0 ? tablePrice / divisor : tablePrice;
}

/**
 * Retorna a alíquota de ICMS (fração, ex.: 0.12) para o UF do pedido.
 * Usa icmsRates do GET /taxes/config quando disponível; senão fallback (PR 12%, demais 4%).
 */
function getIcmsRateForUf(orderUf: string): number {
  const uf = (orderUf || '').toUpperCase().trim();
  if (currentIcmsRates?.length) {
    const entry = currentIcmsRates.find((r) => (r.state || '').toUpperCase() === uf);
    if (entry != null && Number.isFinite(entry.rate)) {
      return entry.rate / 100;
    }
  }
  return uf === companyInfo.uf ? ICMS_INTERNO_PR : ICMS_IMPORTADO;
}

/** Mesma alíquota acima, só que com a assinatura antiga (product é ignorado — mantido pelos chamadores existentes). */
export function getIcmsRate(_product: Product, orderUf: string): number {
  return getIcmsRateForUf(orderUf);
}

/**
 * Divisor da fórmula de preço líquido (1 - PIS - COFINS - ICMS do UF de destino) — ver
 * comentário de ICMS_IMPORTADO/ICMS_INTERNO_PR acima pro histórico completo.
 */
function getPriceDivisor(orderUf: string): number {
  return 1 - 0.0165 - 0.076 - getIcmsRateForUf(orderUf);
}

/**
 * Get tax rule details for display. icmsOriginDescription usa a alíquota dinâmica quando disponível.
 */
export function getTaxRule(product: Product, orderUf: string, isConsumerFinal: boolean): TaxRule {
  const uf = (orderUf || '').toUpperCase();
  const isInterstate = uf !== companyInfo.uf;
  const icmsRate = getIcmsRate(product, orderUf);
  const percent = Math.round(icmsRate * 100);
  const icmsOriginDescription = `${uf || 'UF'} (${percent}%)`;

  let cfop: string;
  if (isInterstate) {
    cfop = '6.101'; // Venda interestadual
  } else {
    cfop = '5.101'; // Venda interna
  }

  return {
    icmsRate,
    icmsOriginDescription,
    cfop,
    isInterstate,
    difalApplicable: isInterstate && isConsumerFinal,
  };
}

/**
 * Round quantity to packaging unit
 */
export function roundToPackagingUnit(quantity: number, packagingUnit: number, fraction: number): number {
  if (quantity <= 0) return 0;

  // Se houver unidade de embalagem definida, aplica lógica tipo TETO:
  // arredonda a quantidade para o menor múltiplo de packagingUnit que
  // seja maior ou igual à quantidade solicitada (espelho da planilha: TETO(J;H)).
  if (packagingUnit > 0) {
    const units = Math.ceil(quantity / packagingUnit);
    return units * packagingUnit;
  }

  // Caso não haja unidade de embalagem, mantém o arredondamento pelo tamanho do "fraction".
  if (fraction > 0) {
    const fractions = Math.ceil(quantity / fraction);
    return fractions * fraction;
  }

  return quantity;
}

/**
 * Quebra da quantidade pedida em pacotes fechados (perfis/caixas) + unidades soltas
 * (barras/peças), para a lista de separação. packagingUnit é o tamanho do pacote fechado
 * e fraction o tamanho de 1 barra/peça, ambos na mesma unidade do item; sem fraction,
 * conta pacotes inteiros arredondando para cima.
 * Ex.: packagingUnit 23,20 / fraction 5,80 (4 barras por pacote), quantidade 63,80
 * -> 2 perfis fechados e 3 barras soltas.
 */
export function calculatePackageBreakdown(
  quantity: number,
  packagingUnit: number,
  fraction: number
): { closedPackages: number; looseUnits: number } {
  if (quantity <= 0 || packagingUnit <= 0) return { closedPackages: 0, looseUnits: 0 };

  const unitSize = fraction > 0 ? fraction : packagingUnit;
  const ratio = packagingUnit / unitSize;
  // O pacote não é feito de barras/peças inteiras (ex.: caixa/rolo de 200 m com barra de 5,80 m:
  // 200 ÷ 5,80 = 34,48): não existe "solta", só pacotes fechados. Senão 200 m virava "1 cx e 1 pç".
  if (fraction > 0 && Math.abs(ratio - Math.round(ratio)) * unitSize > 0.02) {
    return { closedPackages: Math.ceil(quantity / packagingUnit - 1e-6), looseUnits: 0 };
  }
  const unitsPerPackage = Math.max(1, Math.round(ratio));

  // Arredonda para cima as unidades necessárias, tolerando erro de ponto flutuante
  // (ex.: 4.0000000001 -> 4, não 5).
  const totalUnits = Math.ceil(quantity / unitSize - 1e-6);

  // Sem fraction distinta da embalagem (unitsPerPackage 1), não existe pacote fechado
  // real — cada unidade já é a barra/peça individual, então conta tudo como solto
  // (ex.: embalagem 5,80 sem fraction e quantidade 52,20 -> 9 barras, não 9 "perfis").
  if (unitsPerPackage <= 1) {
    return { closedPackages: 0, looseUnits: totalUnits };
  }

  const closedPackages = Math.floor(totalUnits / unitsPerPackage);
  const looseUnits = totalUnits - closedPackages * unitsPerPackage;
  return { closedPackages, looseUnits };
}

/**
 * Rolo nunca fraciona (decidido com o cliente em 17/08): sempre arredonda pra cima em
 * rolos fechados, nunca sobra "barra" solta — diferente de embalagem/caixa, que podem
 * ter sobra fracionada. Ignora qualquer fraction cadastrada no produto (rolo não deveria
 * ter uma, mas por segurança o cálculo não depende disso).
 */
function wholeRolls(quantity: number, packagingUnit: number): { closedPackages: number; looseUnits: number } {
  if (quantity <= 0 || packagingUnit <= 0) return { closedPackages: 0, looseUnits: 0 };
  return { closedPackages: Math.ceil(quantity / packagingUnit - 1e-6), looseUnits: 0 };
}

/** Nomes de produto que fecham em caixa mesmo sendo vendidos por metro (ex.: borracha em rolo). */
const CAIXA_KEYWORDS = ['borracha'];
/** Nomes de produto que fecham em rolo (não caixa, não embalagem) mesmo sendo vendidos por metro. */
const ROLO_KEYWORDS = ['fita'];

export type ItemGroup = 'reforco' | 'aluminio' | 'acessorio';

/**
 * Grupo do item para destacar a linha na impressão da separação (tons de cinza): reforço, alumínio
 * ou acessório; os demais (perfis de PVC etc.) ficam sem destaque. O cadastro não tem grupo
 * preenchido, então é deduzido pela descrição e pela unidade (acessório = vendido em peça/conjunto).
 */
export function getItemGroup(product?: Product | null): ItemGroup | null {
  const description = (product?.description ?? '').toLowerCase();
  if (/refor[çc]o/.test(description)) return 'reforco';
  if (/alum[ií]n|\balum\b/.test(description)) return 'aluminio';
  const unit = (product?.unit ?? '').toLowerCase();
  if ((unit && unit !== 'm') || /\((pc|un)\)/.test(description)) return 'acessorio';
  return null;
}

export type PackagingCategory = 'caixa' | 'rolo' | 'embalagem';

/** Categoria de fechamento do item: unidade "pç" ou nome com CAIXA_KEYWORDS fecha em caixa;
 * nome com ROLO_KEYWORDS (ex.: fita) fecha em rolo; qualquer outro item em metros fecha em embalagem. */
export function getPackagingCategory(item: OrderItem): PackagingCategory {
  if (item.product?.unit === 'pç') return 'caixa';
  const description = item.product?.description?.toLowerCase() ?? '';
  if (CAIXA_KEYWORDS.some((keyword) => description.includes(keyword))) return 'caixa';
  if (ROLO_KEYWORDS.some((keyword) => description.includes(keyword))) return 'rolo';
  return 'embalagem';
}

/** Item que fecha em caixas (unidade "pç" ou nome com CAIXA_KEYWORDS, ex.: borracha). */
export function isPieceUnit(item: OrderItem): boolean {
  return getPackagingCategory(item) === 'caixa';
}

/**
 * Pacotes fechados + sobra do item (perfis e barras, ou caixas e pç).
 * Usa roundedQuantity (a quantidade que realmente sai na nota/separação), não quantity
 * (a digitada originalmente) — por padrão as duas resultam no mesmo breakdown, já que
 * roundedQuantity é um múltiplo exato da embalagem, mas quando roundedQuantity é editada
 * manualmente pra uma quantidade exata negociada (ver NewOrderForm), pode não bater mais
 * com um múltiplo exato e o pacote fica fracionado (ex.: 2 perfis e 2 barras em vez de 3
 * perfis fechados).
 */
export function itemPackageBreakdown(item: OrderItem): { closedPackages: number; looseUnits: number } {
  if (getPackagingCategory(item) === 'rolo') {
    return wholeRolls(item.roundedQuantity, item.product?.packagingUnit ?? 0);
  }
  return calculatePackageBreakdown(
    item.roundedQuantity,
    item.product?.packagingUnit ?? 0,
    // Sem fraction cadastrada: peça avulsa vale 1 pç; item em metros conta pacote inteiro.
    item.product?.fraction || (isPieceUnit(item) ? 1 : 0)
  );
}

/** Texto formatado do breakdown de pacotes do item (ex.: "2 embalagens e 3 barras", "5 caixas", "3 rolos e 2 barras"). */
export function formatItemPackages(item: OrderItem): string {
  const { closedPackages, looseUnits } = itemPackageBreakdown(item);
  if (closedPackages === 0 && looseUnits === 0) return '—';

  const category = getPackagingCategory(item);
  const parts: string[] = [];
  if (category === 'caixa') {
    if (closedPackages > 0) parts.push(`${closedPackages} caixa${closedPackages > 1 ? 's' : ''}`);
    if (looseUnits > 0) parts.push(`${looseUnits} pç`);
  } else if (category === 'rolo') {
    if (closedPackages > 0) parts.push(`${closedPackages} rolo${closedPackages > 1 ? 's' : ''}`);
    if (looseUnits > 0) parts.push(`${looseUnits} barra${looseUnits > 1 ? 's' : ''}`);
  } else {
    if (closedPackages > 0) parts.push(`${closedPackages} embalage${closedPackages > 1 ? 'ns' : 'm'}`);
    if (looseUnits > 0) parts.push(`${looseUnits} barra${looseUnits > 1 ? 's' : ''}`);
  }
  return parts.join(' e ');
}

/** Igual a getPackagingCategory, mas recebe o produto direto — serve pra chamadores que não
 * têm um OrderItem completo (ex.: PickingItem da tela de logística). */
export function getPackagingCategoryForProduct(product?: Product | null): PackagingCategory {
  if (product?.unit === 'pç') return 'caixa';
  const description = product?.description?.toLowerCase() ?? '';
  if (CAIXA_KEYWORDS.some((keyword) => description.includes(keyword))) return 'caixa';
  if (ROLO_KEYWORDS.some((keyword) => description.includes(keyword))) return 'rolo';
  return 'embalagem';
}

export interface PackageLabelEntry {
  key: string;
  /** 'closed' = 1 pacote/caixa/rolo fechado (1 etiqueta cada); 'loose' = todas as unidades soltas juntas em 1 único pacote físico (1 etiqueta só, mostrando a quantidade). */
  kind: 'closed' | 'loose';
  /** Só relevante pra kind 'loose': quantas unidades soltas (barras/pç) estão nesse pacote. */
  looseCount: number;
  categoryLabel: string; // "Embalagem" | "Caixa" | "Rolo"
  looseUnitLabel: string; // "barra" | "pç"
}

/**
 * Uma etiqueta por pacote físico do item: 1 etiqueta pra cada embalagem/caixa/rolo fechado,
 * mais 1 etiqueta única pras unidades soltas (barras/pç) — mesmo que sejam várias, elas vão
 * juntas em 1 pacote físico só, então levam 1 etiqueta só, indicando a quantidade.
 * Ex.: 2 embalagens fechadas + 4 barras soltas -> 3 etiquetas (2 "Embalagem" + 1 "4 barras").
 */
export function buildPackageLabelEntries(
  product: Product | undefined | null,
  quantity: number
): PackageLabelEntry[] {
  const category = getPackagingCategoryForProduct(product);
  const packagingUnit = product?.packagingUnit ?? 0;
  const { closedPackages, looseUnits } =
    category === 'rolo'
      ? wholeRolls(quantity, packagingUnit)
      : calculatePackageBreakdown(quantity, packagingUnit, product?.fraction || (category === 'caixa' ? 1 : 0));

  const categoryLabel = category === 'caixa' ? 'Caixa' : category === 'rolo' ? 'Rolo' : 'Embalagem';
  const looseUnitLabel = category === 'caixa' ? 'pç' : 'barra';

  const entries: PackageLabelEntry[] = [];
  for (let i = 0; i < closedPackages; i++) {
    entries.push({ key: `closed-${i}`, kind: 'closed', looseCount: 0, categoryLabel, looseUnitLabel });
  }
  if (looseUnits > 0) {
    entries.push({ key: 'loose', kind: 'loose', looseCount: looseUnits, categoryLabel, looseUnitLabel });
  }
  return entries;
}

/** Texto padrão (editável depois) de uma etiqueta de pacote. */
export function defaultPackageLabelText(product: Product | undefined | null, entry: PackageLabelEntry): string {
  const code = product?.codePreference || product?.codeOmie || '';
  const desc = product?.description || '';
  const color = product?.color ? ` (${product.color})` : '';
  const suffix =
    entry.kind === 'closed'
      ? entry.categoryLabel
      : `${entry.looseCount} ${entry.looseUnitLabel}${entry.looseCount > 1 ? 's' : ''}`;
  return [`${code} - ${desc}${color}`.trim(), suffix].filter(Boolean).join(' — ');
}

/**
 * Calculate net price based on payment condition and commercial discount.
 * Para pagamento à vista, aplica fórmula O*(1-P5)/DIVISOR_PRECO_A_VISTA para bater com a planilha.
 */
type DiscountConfig = {
  cashDiscountRate?: number; // fração, ex.: 0.4559
  termDiscountRate?: number; // fração, ex.: 0.412
} | null;

let currentDiscountConfig: DiscountConfig = null;

/** Define as taxas de desconto dinâmicas vindas da API (/taxes/config). */
export function setTaxDiscountConfig(config: DiscountConfig) {
  currentDiscountConfig = config;
}

function getCashDiscountRate(): number {
  if (currentDiscountConfig?.cashDiscountRate != null) {
    return currentDiscountConfig.cashDiscountRate;
  }
  return DISCOUNT_AVISTA;
}

function getTermDiscountRate(): number {
  if (currentDiscountConfig?.termDiscountRate != null) {
    return currentDiscountConfig.termDiscountRate;
  }
  return DISCOUNT_APRAZO;
}

export function calculateNetPrice(
  tablePrice: number,
  paymentCondition: PaymentCondition,
  commercialDiscount: number = 0,
  customDiscountRate?: number,
  /** UF de destino do pedido — define a alíquota de ICMS usada no divisor (ver getPriceDivisor). */
  orderUf: string = companyInfo.uf,
  /**
   * Desconto adicional (fração 0-1), à parte da condição de pagamento — vem de planilhas
   * de pedido (ex.: "Com 10%" perto da célula que diz à vista/a prazo) e multiplica em cima
   * do preço já calculado com o desconto padrão (bate com a fórmula da planilha, tipo
   * ValorComDescontoAdicional = ValorPadrão × 0,9 pra 10%). Decidido com o cliente em 24/09.
   */
  additionalDiscountRate?: number,
): number {
  let price = tablePrice;
  const divisor = getPriceDivisor(orderUf);

  if (paymentCondition === 'cash') {
    // 1) aplica desconto de P5 (cashDiscountRate) vindo do backend
    const cashRate = getCashDiscountRate(); // ex.: 0.4559 (45,59%)
    price = price * (1 - cashRate);

    // 2) divide pelo divisor da fórmula do Excel (PIS+COFINS+ICMS do UF de destino)
    if (divisor > 0) {
      price = price / divisor;
    }
  } else if (paymentCondition === 'credit') {
    const termRate = getTermDiscountRate(); // ex.: 0.412
    price = price * (1 - termRate);

    // Mesmo divisor da planilha: (1-0,0165-0,076-ICMS do UF)
    if (divisor > 0) {
      price = price / divisor;
    }
  } else if (paymentCondition === 'custom' && customDiscountRate != null) {
    // Condição especial: desconto personalizado (fração, ex.: 0.38 = 38%)
    price = price * (1 - customDiscountRate);

    if (divisor > 0) {
      price = price / divisor;
    }
  }

  if (additionalDiscountRate != null && additionalDiscountRate > 0) {
    price = price * (1 - additionalDiscountRate);
  }

  if (commercialDiscount > 0) {
    price = price * (1 - commercialDiscount / 100);
  }

  return price;
}

/**
 * Calculate DIFAL for B2C sales
 */
export function calculateDifal(
  productValue: number,
  clientUf: string,
  isConsumerFinal: boolean,
  icmsRate: number
): number {
  if (!isConsumerFinal || clientUf === companyInfo.uf) {
    return 0;
  }
  
  // DIFAL = (Internal rate - Interstate rate) * Product value
  const difalRate = ICMS_INTERNO_PR - icmsRate;
  return productValue * difalRate;
}

/**
 * Calculate order item totals
 */
export function calculateItemTotals(
  product: Product,
  quantity: number,
  paymentCondition: PaymentCondition,
  commercialDiscount: number,
  clientUf: string,
  isConsumerFinal: boolean,
  customDiscountRate?: number,
  /** Desconto adicional (fração 0-1), à parte — ver calculateNetPrice. */
  additionalDiscountRate?: number,
  /** Sobrescreve a quantidade arredondada calculada (venda de quantidade exata negociada com o cliente). */
  roundedQuantityOverride?: number,
  /** Sobrescreve o preço líquido calculado (preço especial negociado para este item/pedido). */
  netPriceOverride?: number,
): Omit<OrderItem, 'id' | 'productId' | 'product'> {
  const roundedQuantity = roundedQuantityOverride ?? roundToPackagingUnit(quantity, product.packagingUnit, product.fraction);
  const basePrice = getBasePriceForOrder(product.tablePrice, clientUf, isConsumerFinal);
  const netPriceRaw = netPriceOverride ?? calculateNetPrice(basePrice, paymentCondition, commercialDiscount, customDiscountRate, clientUf, additionalDiscountRate);
  const netPrice = Math.round(netPriceRaw * 100) / 100;
  // Valor total do item: espelho da planilha PrefGest / Espelho NFe
  // Usa o valor cheio de netPriceRaw (como o Excel faz) multiplicado pela quantidade arredondada
  // e arredonda o resultado final para 2 casas decimais.
  const totalValue = Math.round(netPriceRaw * roundedQuantity * 100) / 100;
  const ipiValue = Math.round(totalValue * (product.ipiRate / 100) * 100) / 100;
  const weight = roundedQuantity * product.unitWeight;

  // Debug: inspecionar como o total do item está sendo calculado em relação à planilha PrefGest.
  // Pode ser removido depois de validar os cálculos.
  // eslint-disable-next-line no-console
  console.log('[calculateItemTotals]', {
    productId: product.id,
    tablePrice: product.tablePrice,
    paymentCondition,
    commercialDiscount,
    quantityDigitada: quantity,
    quantityArredondada: roundedQuantity,
    netPriceRaw,
    netPrice,
    totalValue,
  });

  return {
    quantity,
    roundedQuantity,
    commercialDiscount,
    tablePrice: product.tablePrice,
    netPrice,
    totalValue,
    ipiValue,
    weight,
  };
}

/**
 * Calculate order summary
 */
export function calculateOrderSummary(
  items: OrderItem[],
  clientUf: string,
  isConsumerFinal: boolean
) {
  const subtotalProducts = items.reduce((sum, item) => sum + item.totalValue, 0);
  const totalIpi = items.reduce((sum, item) => sum + item.ipiValue, 0);
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
  
  // Calculate ICMS and DIFAL
  let totalIcms = 0;
  let totalDifal = 0;
  
  items.forEach((item) => {
    const icmsRate = getIcmsRate(item.product, clientUf);
    const itemIcms = item.totalValue * icmsRate;
    totalIcms += itemIcms;
    
    if (isConsumerFinal) {
      totalDifal += calculateDifal(item.totalValue, clientUf, isConsumerFinal, icmsRate);
    }
  });
  
  return {
    subtotalProducts,
    totalIpi,
    totalIcms,
    totalDifal,
    totalWeight,
  };
}

/**
 * Format currency to Brazilian Real
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Format number with Brazilian locale
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format weight in kg
 */
export function formatWeight(value: number): string {
  return `${formatNumber(value, 2)} kg`;
}
